// Mesajlar — sağ bölme: konuşma başlığı, mesaj akışı (en yeni altta), yazma alanı.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Button, CircularProgress, IconButton, ListItemText, Menu, MenuItem, Skeleton, Typography, alpha } from "@mui/material";
import {
  ArrowBackRounded as BackIcon,
  ChatBubbleOutlineRounded as ChatIcon,
  OpenInNewRounded as OpenIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { ActionButton, EmptyState, SegmentedControl } from "../../components/atoms";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { avatarTone, fullName, initialsOf } from "../Secretary/PatientsPage/patientListFormat";
import { buildTimeline } from "./messageFormat";
import { WINDOW_CLOSED_NOTE } from "../../constants/whatsapp";
import MessageBubble from "./MessageBubble";
import MessageComposer from "./MessageComposer";
import useTerms from "../../hooks/useTerms";

const CHANNEL_OPTIONS = [
  { value: "app", label: "Uygulama" },
  { value: "whatsapp", label: "WhatsApp" },
];

function ThreadSkeleton() {
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {[60, 40, 70, 35].map((w, i) => (
        <Box key={i} sx={{ display: "flex", justifyContent: i % 2 ? "flex-end" : "flex-start" }}>
          <Skeleton variant="rounded" width={`${w}%`} height={44} sx={{ borderRadius: 2.5 }} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * @param {object|null} thread - sunucu yanıtı { conversation, patient, messages, hasMore, canSend }
 * @param {boolean} loading
 * @param {boolean} error
 * @param {object[]} olderMessages - "Daha eski mesajlar" ile gelenler
 * @param {boolean} hasMoreOlder
 * @param {boolean} loadingOlder
 * @param {() => void} onLoadOlder
 * @param {(text: string, channel: "app"|"whatsapp") => Promise<boolean>} onSend - başarılıysa true
 * @param {boolean} sending
 * @param {(templateKey: string) => void} [onSendTemplate] - pencere kapalıyken seçilen manuel WhatsApp şablonu
 * @param {boolean} [sendingTemplate]
 * @param {boolean} canSendPermission - messages:send
 * @param {() => void} [onBack] - dar ekranda listeye dönüş
 * @param {() => void} [onOpenPatient] - patients:read varsa hasta sayfası
 * @param {boolean} [hideHeader] - başlık (ad + avatar) çizilmez; hasta detayında zaten hastanın sayfasındayız
 * @param {object} palette
 */
export default function MessageThread({
  thread,
  loading,
  error,
  olderMessages,
  hasMoreOlder,
  loadingOlder,
  onLoadOlder,
  onSend,
  sending,
  onSendTemplate,
  sendingTemplate = false,
  canSendPermission,
  onBack,
  onOpenPatient,
  hideHeader = false,
  palette,
}) {
  const terms = useTerms();
  const [channel, setChannel] = useState("app");
  const [templateAnchor, setTemplateAnchor] = useState(null);
  const scrollerRef = useRef(null);
  const lastKeyRef = useRef(null);
  const prevHeightRef = useRef(null);

  const timeline = useMemo(
    () => buildTimeline([olderMessages || [], thread?.messages || []]),
    [olderMessages, thread?.messages]
  );
  const newestKey = timeline.length ? timeline[timeline.length - 1].key : null;
  const oldestKey = timeline.length ? timeline[0].key : null;
  const patient = thread?.patient;
  const linked = thread?.canSend !== false;

  // Yeni mesaj gelince (ya da konu açılınca) en alta kaydır; eski sayfa
  // eklenince okunan yer korunur.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (newestKey && newestKey !== lastKeyRef.current) {
      el.scrollTop = el.scrollHeight;
      lastKeyRef.current = newestKey;
    } else if (prevHeightRef.current != null) {
      el.scrollTop = el.scrollHeight - prevHeightRef.current;
    }
    prevHeightRef.current = null;
  }, [newestKey, oldestKey]);

  // Konu değişince kaydırma ve kanal sıfırlanır (taslağı MessageComposer temizler)
  const threadKey = thread?.conversation?._id || patient?._id || null;
  const wa = thread?.whatsapp || null;
  const channelChoice = Boolean(wa?.connected && wa?.hasPhone);
  const lastChannel = thread?.conversation?.lastChannel;
  useEffect(() => {
    lastKeyRef.current = null;
  }, [threadKey]);
  // Varsayılan: hastanın son mesajının kanalı. Konu açılınca bir kez seçilir;
  // periyodik yenilemede kullanıcının seçimi ezilmez.
  const defaultedRef = useRef(null);
  useEffect(() => {
    const key = `${threadKey}|${channelChoice}`;
    if (defaultedRef.current === key) return;
    defaultedRef.current = key;
    setChannel(channelChoice && lastChannel === "whatsapp" ? "whatsapp" : "app");
  }, [threadKey, channelChoice, lastChannel]);

  const handleOlder = () => {
    prevHeightRef.current = scrollerRef.current?.scrollHeight ?? null;
    onLoadOlder();
  };

  const viaWhatsApp = channelChoice && channel === "whatsapp";
  const windowClosed = viaWhatsApp && wa?.windowOpen === false;

  if (loading && !thread) {
    return <ThreadSkeleton />;
  }
  if (error && !thread) {
    return (
      <EmptyState
        variant="illustrated"
        icon={<ChatIcon />}
        title="Konuşma açılamadı"
        description={`Bu konuşmaya erişilemiyor. ${terms.patient} kliniğinize bağlı olmayabilir.`}
        sx={{ flex: 1 }}
      />
    );
  }
  if (!thread) return null;

  const tone = avatarTone(patient);
  let footerNote = null;
  if (!canSendPermission) footerNote = "Mesaj gönderme yetkiniz yok.";
  else if (!linked) footerNote = `${terms.patient} artık kliniğinize bağlı değil; yeni mesaj gönderilemez.`;
  else if (windowClosed) footerNote = WINDOW_CLOSED_NOTE;

  const canCompose = canSendPermission && linked;
  const channelSelector =
    canCompose && channelChoice ? (
      <SegmentedControl
        ariaLabel="Gönderim kanalı"
        value={channel}
        onChange={setChannel}
        options={CHANNEL_OPTIONS}
        palette={palette}
      />
    ) : null;
  // Pencere kapalıyken serbest metin yerine hazır şablon: menüden seçilen anında gider.
  const templates = wa?.templates || [];
  const pickTemplate = (key) => {
    setTemplateAnchor(null);
    onSendTemplate?.(key);
  };
  const pingAction =
    canCompose && windowClosed && onSendTemplate ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
        <ActionButton
          variant="send"
          tone="soft"
          palette={palette}
          icon={<WhatsAppIcon sx={{ fontSize: 15 }} />}
          label="WhatsApp şablonu gönder"
          loading={sendingTemplate}
          disabled={wa?.optedIn === false || templates.length === 0}
          onClick={(e) => setTemplateAnchor(e.currentTarget)}
        />
        <Menu
          anchorEl={templateAnchor}
          open={Boolean(templateAnchor)}
          onClose={() => setTemplateAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: { width: 420, maxWidth: "calc(100vw - 32px)", borderRadius: 2.5, mt: -1 } } }}
        >
          {templates.map((t) => (
            <MenuItem
              key={t.key}
              disabled={!t.approved}
              onClick={() => pickTemplate(t.key)}
              sx={{ alignItems: "flex-start", whiteSpace: "normal", py: 1.25, gap: 1 }}
            >
              <WhatsAppIcon sx={{ fontSize: 16, mt: "3px", color: t.approved ? palette.primary : COLORS.slate400 }} />
              <ListItemText
                primary={t.title}
                secondary={t.approved ? t.body : t.marketing ? "Meta pazarlama kategorisine aldı; gönderilemez" : "Meta onayı bekleniyor"}
                primaryTypographyProps={{ sx: { fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 } }}
                secondaryTypographyProps={{ sx: { fontSize: TYPO.label, color: COLORS.slate500, lineHeight: 1.4 } }}
              />
            </MenuItem>
          ))}
        </Menu>
        {wa?.optedIn === false ? (
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>Hastanın WhatsApp onayı yok</Typography>
        ) : null}
      </Box>
    ) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Başlık */}
      {hideHeader ? null : (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 1.5, md: 3 },
          py: 1.5,
          borderBottom: `1px solid ${COLORS.slate200}`,
          flexShrink: 0,
        }}
      >
        {onBack ? (
          <IconButton size="small" aria-label="Konuşmalara dön" onClick={onBack}>
            <BackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ) : null}
        <Avatar sx={{ width: 36, height: 36, fontSize: "0.8125rem", fontWeight: 600, bgcolor: alpha(tone, 0.14), color: tone }}>
          {initialsOf(patient)}
        </Avatar>
        <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: TYPO.title, fontWeight: 600, color: COLORS.slate800 }}>
          {fullName(patient)}
        </Typography>
        {onOpenPatient ? (
          <Button
            size="small"
            onClick={onOpenPatient}
            endIcon={<OpenIcon sx={{ fontSize: 15 }} />}
            sx={{ textTransform: "none", fontSize: TYPO.label, color: palette.primary, px: 1.5, flexShrink: 0 }}
          >
            {terms.patient} sayfası
          </Button>
        ) : null}
      </Box>
      )}

      {/* Mesaj akışı */}
      <Box
        ref={scrollerRef}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", px: { xs: 2, md: 3 }, py: 2 }}
      >
        {hasMoreOlder ? (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Button
              size="small"
              onClick={handleOlder}
              disabled={loadingOlder}
              startIcon={loadingOlder ? <CircularProgress size={12} color="inherit" /> : null}
              sx={{ textTransform: "none", fontSize: TYPO.label, color: COLORS.slate500, px: 1.5 }}
            >
              Daha eski mesajlar
            </Button>
          </Box>
        ) : null}

        {timeline.length === 0 ? (
          <EmptyState
            variant="illustrated-sm"
            icon={<ChatIcon />}
            title="Henüz mesaj yok"
            description={
              linked && canSendPermission
                ? `İlk mesajı yazarak yazışmayı başlatın. ${terms.patientDat} bildirim gider.`
                : `Bu ${terms.patientInsLower} henüz yazışma yapılmadı.`
            }
          />
        ) : (
          timeline.map((item) =>
            item.type === "day" ? (
              <Box key={item.key} sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
                <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, px: 1.25, py: 0.25, borderRadius: 10, bgcolor: COLORS.slate100 }}>
                  {item.label}
                </Typography>
              </Box>
            ) : (
              <MessageBubble key={item.key} message={item.message} palette={palette} />
            )
          )
        )}
      </Box>

      {/* Yazma alanı */}
      <MessageComposer
        palette={palette}
        resetKey={threadKey || ""}
        sending={sending}
        disabled={!canCompose || windowClosed}
        note={footerNote}
        top={channelSelector}
        extra={pingAction}
        onSend={(text) => onSend(text, viaWhatsApp ? "whatsapp" : "app")}
      />
    </Box>
  );
}
